import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import {
  IOpsViewerMessagesService,
  UserAuthorizationService,
  IApplicationContextService
} from '../../../services/services';
import { BehaviorSubject, combineLatest, firstValueFrom, from, map, Observable, shareReplay, Subject, Subscription, switchMap, takeUntil, tap, catchError, of, finalize, filter } from 'rxjs';
import { IUserManagerService } from '../../../services/iUserManager.service';
import { ICurrentUserService } from '../../../services/currentUserService/icurrentuser.service';
import { CompanyClient, CompanyNameViewModel, ConsumerJob, DeviceClient, IConsumerJobClient, IVendorJobClient, User, VendorJob } from '../../../services/nswag/nswagclient';
import { JobCacheService } from '../../../services/job-cache.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FilePopUpComponent } from '../../files/file-pop-up/file-pop-up.component';
import { TelemetryService } from '../../../services/telemetry.service';
import { LoadingService } from '../../../services/loading.service';

interface CompanyWithJobs {
  companyId: string;
  companyName: string;
  jobs: VendorJob[];
}

type CompanyContext = CompanyNameViewModel & { companyId: string };

@Component({
  selector: 'app-vendor-consumer-jobs',
  standalone: false,
  templateUrl: './vendor-consumer-jobs.component.html',
  styleUrls: ['./vendor-consumer-jobs.component.scss']
})
export class VendorConsumerJobsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  vendorJobCols: any[] = [];

   consumerJobCols: any[] = [];

  isUserCompanyVendor = false;
  isUserCompanyConsumer = false;

  securityToken = '';
  ref: DynamicDialogRef | null = null;
  constructor(
    @Inject("IVendorJobClient") private _vendorJobService: IVendorJobClient,
    @Inject("IConsumerJobClient") private _consumerJobService: IConsumerJobClient,
    private _userAuthorizationService: UserAuthorizationService,
    private _companyService: CompanyClient,
    private _applicationContextService: IApplicationContextService,
    private _messageService: IOpsViewerMessagesService,
    private _userManagerService: IUserManagerService,
    private _titleService: Title,
    private _router: Router,
    private _confirmationService: ConfirmationService,
    //private _deviceService: IDeviceService,
    private _deviceClient: DeviceClient,
    private _currentUserService: ICurrentUserService,
    private _jobsCacheService: JobCacheService,
    private _dialogService: DialogService,
    private _telemetryService: TelemetryService,
    private _loadingService: LoadingService
  ) { 
   
  }

  company$!: Observable<CompanyContext>;
  vendorCompanies$!: Observable<CompanyNameViewModel[]>;
  user$!: Observable<User>;
  vendorJobs$!: Observable<VendorJob[]>;
  consumerJobs$!: Observable<ConsumerJob[]>;
  groupedVendorJobs$!: Observable<CompanyWithJobs[]>;
  hasNoJobs$!: Observable<boolean>;
  isUserCompanyVendor$!: Observable<boolean>;
  isUserCompanyConsumer$!: Observable<boolean>;
  roles$!: Observable<UserRoles>;
  applicationBarSubscription?: Subscription;
  deletedId$ = new BehaviorSubject<string[]>([]);
  // Loading state observables
  isLoadingVendorJobs$ = this._loadingService.getLoading('vendorJobs');
  isLoadingConsumerJobs$ = this._loadingService.getLoading('consumerJobs');
  isLoadingVendorCompanies$ = this._loadingService.getLoading('vendorCompanies');
  isLoadingConsumerCompanies$ = this._loadingService.getLoading('consumerCompanies');
  isLoadingDevices$ = this._loadingService.getLoading('devices');
  isLoadingDeleteJob$ = this._loadingService.getLoading('deleteJob');
  isLoadingDashboardLink$ = this._loadingService.getLoading('dashboardLink');
  isLoadingJobData$ = this._loadingService.getLoading('jobData');
  isLoadingOverall$ = this._loadingService.getAnyLoading(['vendorJobs', 'consumerJobs', 'vendorCompanies', 'consumerCompanies', 'devices']);
  isLoadingAny$ = this._loadingService.getAnyLoading(['vendorJobs', 'consumerJobs', 'vendorCompanies', 'consumerCompanies', 'devices', 'deleteJob', 'dashboardLink', 'jobData']);
  
  ngOnInit() {    this.user$ = this._userManagerService.getCurrentUser();
    this.company$ = this._applicationContextService.companyContextObserver.pipe(takeUntil(this.destroy$), tap(c => {
      if (!c) {
        console.warn('Company Context Changed to null. This can only happen if they are not assigned to a company.');
        this._telemetryService.trackTrace({message: 'Waring!: Company Context Changed to null. This can only happen if they are not assigned to a company.'});
      }
      console.log('Company Context Changed', c);
    }), filter((c): c is CompanyContext => Boolean(c?.companyId)), shareReplay(1));    // Vendor companies with loading
    this.vendorCompanies$ = this._companyService.getVendorCompanyNames().pipe(
      tap(() => this._loadingService.setLoading('vendorCompanies', true)),
  //    take(1), // Force completion after first emission
      tap((result) => console.log('Vendor companies loaded:', result.length)),
      finalize(() => this._loadingService.setLoading('vendorCompanies', false)),
      catchError(error => {
        console.error('Error loading vendor companies:', error);
        return of([]);
      }),
      shareReplay(1)
    );
    
    // Consumer companies with loading
    const consumerCompanies$ = this._companyService.getConsumerCompanyNames().pipe(
      tap(() => this._loadingService.setLoading('consumerCompanies', true)),
     // take(1), // Force completion after first emission
      tap((result) => console.log('Consumer companies loaded:', result.length)),
      finalize(() => this._loadingService.setLoading('consumerCompanies', false)),
      catchError(error => {
        console.error('Error loading consumer companies:', error);
        return of([]);
      }),
      shareReplay(1)
    );// Consumer jobs with loading
    const consumerJobs$ = this.company$.pipe(
      tap(c=>{console.debug(c)}),
      switchMap(c => {
        this._loadingService.setLoading('consumerJobs', true);
        return from(this._jobsCacheService.getConsumerJobs(c.companyId)).pipe(
         // take(1), // Force completion after first emission
          tap((result) => console.log('Consumer jobs loaded:', result.length)),
          finalize(() => this._loadingService.setLoading('consumerJobs', false)),
          catchError(error => {
            console.error('Error loading consumer jobs:', error);
            return of([]);
          })
        );
      }),
      shareReplay(1)
    );
    
    // Devices with loading
    const devices$ = this.company$.pipe(
      switchMap(c => {
        this._loadingService.setLoading('devices', true);
        return this._deviceClient.getDevicesForCompanyIncludingDeleted(c.companyId).pipe(
         // take(1), // Force completion after first emission
          tap((result) => console.log('Devices loaded:', result.length)),
          finalize(() => this._loadingService.setLoading('devices', false)),
          catchError(error => {
            console.error('Error loading devices:', error);
            return of([]);
          })
        );
      }),
      shareReplay(1)
    );

    // Vendor jobs with loading
    const vendorJobs$ = this.company$.pipe(
      switchMap(c => {
        this._loadingService.setLoading('vendorJobs', true);
        return from(this._jobsCacheService.getVendorJobsForVendorCompany(c.companyId)).pipe(
          //take(1), // Force completion after first emission
          tap((result) => console.log('Vendor jobs loaded:', result.length)),
          finalize(() => this._loadingService.setLoading('vendorJobs', false)),
          catchError(error => {
            console.error('Error loading vendor jobs:', error);
            return of([]);
          })
        );
      }),
      shareReplay(1)
    );

    const vendorCompaniesAndJobsAndDevices$ = combineLatest([consumerCompanies$, this.vendorCompanies$, vendorJobs$, devices$, this.deletedId$]);

    const agumentedVendorJobs$ = vendorCompaniesAndJobsAndDevices$.pipe(map(([consumerCompanies, vendorCompanies, jobs, devices, deleted]) => {
      const filteredJobs = jobs.filter(j => !j.id || !deleted.includes(j.id));
      for (const j of filteredJobs) {
        let deviceNames = '';
        const company = consumerCompanies.find(c => c.companyId === j.consumerCompanyId);
        if (company) {
          (j as any).consumerCompanyName = company.companyName;
        }
        const vendorCompany = vendorCompanies.find(c => c.companyId === j.companyId);
        if (vendorCompany) {
          (j as any).vendorCompanyName = vendorCompany.companyName;
        }
        (j.deviceList ?? []).forEach(id => {
          const foundDeviceName = devices.find(d => d.id === id);
          const deviceName = foundDeviceName?.name ?? '';
          if (deviceName) {
            deviceNames = deviceNames.length > 0 ? deviceNames.concat(', ' + deviceName) : deviceNames.concat(deviceName);
          }
        });
        (j as any).deviceNamesAsString = deviceNames;
      }
      filteredJobs.sort((a, b) => {
        const aStart = a.startTime?.getTime() ?? 0;
        const bStart = b.startTime?.getTime() ?? 0;
        if (aStart > bStart) {
          return -1;
        } else if (aStart < bStart) {
          return 1;
        }
        return 0;
      });
      return filteredJobs;
    }));
    this.vendorJobs$ = agumentedVendorJobs$;

     const grouped$ = agumentedVendorJobs$.pipe(
      map(jobs => {
        const grouped: { [key: string]: VendorJob[] } = {};
        jobs.forEach(job => {
          const key = job.companyId;
          if (!key) {
            return;
          }
          if (!grouped[key]) {
            grouped[key] = [];
          }
          grouped[key].push(job);
        });
        return grouped;
      }));
    
    this.groupedVendorJobs$  = combineLatest([grouped$, this.company$, this.vendorCompanies$]).
      pipe(map(([groupedJobs, currentCompany, vendorCompanies]) => {
        const results: CompanyWithJobs[] = [];

        //sort by the companyname
        const sortedKeys = Object.keys(groupedJobs).sort((a, b) => {
          if (a === currentCompany.companyId) {
            return -1; // current company should be first
          }
          if (b === currentCompany.companyId) {
            return 1; // current company should be first
          }
          const companyA = vendorCompanies.find(c => c.companyId === a);
          const companyB = vendorCompanies.find(c => c.companyId === b);
          if (!companyA || !companyB) {
            console.warn(`Company not found for id: ${a} or ${b}`);
            return 0; // if company not found, keep original order
          }
          return (companyA.companyName ?? '').localeCompare(companyB.companyName ?? '');
        })
        sortedKeys.forEach(key => 
            results.push({
              companyId: key,
              companyName: vendorCompanies.find(c => c.companyId === key)?.companyName ?? 'Unknown',
              jobs: groupedJobs[key] || []
          }));
        return results; 
      }), shareReplay(1));

    const consumerJobsAndCompanies$ = combineLatest([consumerJobs$, consumerCompanies$, this.vendorCompanies$, this.company$]);

    const agumentedConsumerJobs = consumerJobsAndCompanies$.pipe(map(([jobs, consumerCompanies, vendorCompanies, currentCompany]) => {
      const results = jobs;//.map(j => Object.assign(new ConsumerJob(), j) as ConsumerJob);
      for (const j of results) {
        const company = consumerCompanies.find(c => c.companyId === j.consumerCompanyId);
        if (company) {
          (j as any).consumerCompanyName = company.companyName;
        }
        // Get VendorCompany Names
        if (j.vendorCompanyIds) {
          const foundVendorCompanies = vendorCompanies.filter(v => v.companyId && j.vendorCompanyIds?.includes(v.companyId));
          if (foundVendorCompanies) {
            (j as any).vendorCompanyNames = foundVendorCompanies.map(v => v.companyName ?? '').join(',');
          }
        }
      }

      const currentCompanyId = currentCompany.companyId;
      const visibleJobs  = results.filter(j =>
        j.consumerCompanyId === currentCompanyId || (j.vendorCompanyIds ?? []).includes(currentCompanyId)
      );

      visibleJobs.sort((a, b) => {
        const aStart = a.startTime?.getTime() ?? 0;
        const bStart = b.startTime?.getTime() ?? 0;
        if (aStart > bStart) {
          return -1;
        } else if (aStart < bStart) {
          return 1;
        }
        return 0;
      });
      return visibleJobs;

    }
    ));
    this.consumerJobs$ = agumentedConsumerJobs;
    this.isUserCompanyVendor$ = combineLatest([this.company$, this.vendorCompanies$]).pipe(map(([company, companies]) => {
      if (!company) { //no company context
        return false;
      }
      const companyInfo = companies.find(c => c.companyId === company.companyId);
      if (!companyInfo) {
        return false;
      }
      return companyInfo.companyType === "Service Provider";
    }));
    this.isUserCompanyConsumer$ = combineLatest([this.company$, consumerCompanies$]).pipe(map(([company, companies]) => {
      if (!company) { //no company context
        return false;
      }
      const companyInfo = companies.find(c => c.companyId === company.companyId);
      if (!companyInfo) {
        return false;
      }
      return companyInfo.companyType === "Operator";
    }));
    this._titleService.setTitle('OpsViewer Jobs');

    this.roles$ = this.user$.pipe(map(u => new UserRoles(u, this._userAuthorizationService)));

    this.applicationBarSubscription = combineLatest([this.isUserCompanyVendor$, this.isUserCompanyConsumer$]).subscribe(([isVendor, isConsumer]) => {
      if (!isVendor && !isConsumer) {
        this._applicationContextService.showApplicationRequestsBar();
      } else {
        this._applicationContextService.hideApplicationRequestsBar();
      }
    });
    
    this.hasNoJobs$ = combineLatest([this.vendorJobs$, this.consumerJobs$]).pipe(
      map(([vendorJobs, consumerJobs]) => {
        const hasNoVendorJobs = vendorJobs.length === 0;
        const hasNoConsumerJobs = consumerJobs.length === 0;
        const hasNoJobs = hasNoVendorJobs && hasNoConsumerJobs;
        console.log('hasNoJobs', hasNoJobs);
        return hasNoJobs;
      }),
    );

    this.setColumns();

    this.setSecurityToken();
  }
  setSecurityToken() {

    this._currentUserService.getAccessToken().subscribe(s => {
      //console.warn(s);
      this.securityToken = s;
    });

  }
  ngOnDestroy() {
    this._applicationContextService.hideApplicationRequestsBar();
    this.applicationBarSubscription?.unsubscribe();
    //console.log('VendorConsumerJobsComponent destroyed');
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clear all loading states when component is destroyed
    this._loadingService.clearAllLoading();
  }



  setColumns() {
    // TODO: Should Vendor Jobs show devices in a column?
    //       Should Consumer Jobs show Vendors in a column?
    this.vendorJobCols = [
      { field: 'name', header: 'Name', width: '160px', filterMatchMode: 'contains' },
      { field: 'apiNumber', header: 'Api Number', width: '160px', filterMatchMode: 'contains' },
      { field: 'wellInfo.name', header: 'Well Name', width: '180px', filterMatchMode: 'contains' },
      { field: 'consumerCompanyName', header: 'Operator', width: '180px', filterMatchMode: 'contains' },
      { field: 'startTime', header: 'Start', width: '120px' },
      { field: 'endTime', header: 'End', width: '120px' },
      { field: 'deviceNamesAsString', header: 'Devices', width: '180px', filterMatchMode: 'contains' },
      // tslint:disable-next-line:max-line-length
      // { field: 'devices', header: 'Devices', width: '150px', filterMatchMode: 'contains', alternativeFilterField: 'deviceNamesAsString' },
      { field: '', header: '', width: '175px' }
    ];

    this.consumerJobCols = [
      { field: 'number', header: 'Job Number', width: '160px', filterMatchMode: 'contains' },
      { field: 'wellApiNumber', header: 'Api Number', width: '160px', filterMatchMode: 'contains' },
      { field: 'wellName', header: 'Well Name', width: '200px', filterMatchMode: 'contains' },
      { field: 'vendorCompanyNames', header: 'Service Providers', width: '200px', filterMatchMode: 'contains' },
      { field: 'startTime', header: 'Start', width: '150px' },
      { field: 'endTime', header: 'End', width: '150px' },
      { field: '', header: '', width: '175px' }
    ];
  }

  navigateToAddVendorJob() {
    this._router.navigate(['/vendorjob/' + encodeURIComponent('new')]);
  }

  navigateToVendorDetail(jobId: string) {
    if (!jobId) {
      return;
    }
    this._router.navigate(['/vendorjob/' + encodeURIComponent(jobId)]);
  }

  navigateToConsumerDetail(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    this._router.navigate(['/consumerjob/' + encodeURIComponent(jobId)]);
  }

  navigateToVendorWorkbook(job: VendorJob) {
    if (job.useOldWorkbookInReadOnlyMode) {
      this._router.navigate(['/workbook/' + job.id]);
    } else {
      this._router.navigate(['/workbook2/' + job.id]);
    }
  }

  async navigateToVendorDashboard(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    this._loadingService.setLoading('dashboardLink', true);
    try {
      const link = await firstValueFrom(this._vendorJobService.getDashboardLink(jobId), { defaultValue: '' });
      if (link) {
        window.open(link, '_blank');
      } else {
        this._telemetryService.trackTrace({ message: 'No dashboard link found, service method ended without completion, for job id: ' + jobId });
        console.error('No dashboard link found for job id: ' + jobId);
      }
    } catch (error) {
      console.error('Error getting dashboard link:', error);
      this._messageService.errorMessage('Error getting dashboard link', 'Unable to open dashboard');
    } finally {
      this._loadingService.setLoading('dashboardLink', false);
    }
  }

  async navigateToConsumerDashboardFromVendor(consumerJobId: string | undefined) {
    if (!consumerJobId) {
      return;
    }
    this._loadingService.setLoading('dashboardLink', true);
    try {
      const link = await firstValueFrom(this._consumerJobService.getDashboardLink(consumerJobId), { defaultValue: '' });
      if (link) {
        window.open(link, '_blank');
      } else {
        this._telemetryService.trackTrace({ message: 'No dashboard link found, service method ended without completion, for job id: ' + consumerJobId });
        console.error('No dashboard link found for job id: ' + consumerJobId);
      }
    } catch (error) {
      console.error('Error getting dashboard link:', error);
      this._messageService.errorMessage('Error getting dashboard link', 'Unable to open dashboard');
    } finally {
      this._loadingService.setLoading('dashboardLink', false);
    }
  }
  


  navigateToConsumerDashboard(job: ConsumerJob) {
    this._loadingService.setLoading('dashboardLink', true);
    if (job.isMigratedFromBeforeConsumerJobs) {
      if (job.vendorJobIds && job.vendorJobIds.length > 0) {
        this._vendorJobService.getDashboardLink(job.vendorJobIds[0]).pipe(
          finalize(() => this._loadingService.setLoading('dashboardLink', false))
        ).subscribe({
          next: l => {
            window.open(l, "_blank");
          },
          error: error => {
            console.error('Error getting dashboard link:', error);
            this._messageService.errorMessage('Error getting dashboard link', 'Unable to open dashboard');
          }
        });
      } else {
        this._loadingService.setLoading('dashboardLink', false);
        this._messageService.warnMessage('Cannot navigate to Consumer Job Dashboard', '');
      }
    } else {
      if (!job.id) {
        this._loadingService.setLoading('dashboardLink', false);
        this._messageService.warnMessage('Cannot navigate to Consumer Job Dashboard', '');
        return;
      }
      this._consumerJobService.getDashboardLink(job.id).pipe(
        finalize(() => this._loadingService.setLoading('dashboardLink', false))
      ).subscribe({
        next: l => {
          window.open(l, "_blank");
        },
        error: error => {
          console.error('Error getting dashboard link:', error);
          this._messageService.errorMessage('Error getting dashboard link', 'Unable to open dashboard');
        }
      });
    }
  }

  downloadJobExport(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    window.open('/api/vendorjob/export/' + jobId + '?token=' + this.securityToken, '_blank');
  }

  openFilePopUp(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    this.ref = this._dialogService.open(FilePopUpComponent, {
      data: {
        jobId: jobId
      },
      header: 'Manage Files',
      width: '50vw',
      baseZIndex: 10000,
      resizable: true,
      maximizable: true
    });
  }

  downloadJobSummaryExport(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    window.open('/api/vendorjob/exportsummary/' + jobId + '?token=' + this.securityToken, '_blank');
  }

  //#endregion

  //#region Deleting Jobs

  async confirmDeleteVendorJob(job: VendorJob): Promise<void> {
    if (!job.id) {
      return;
    }
    const roles = await this.getUserRoles();
    if (roles.isGlobalAdmin()) {
      if (await this.doesJobHaveData(job.id)) {
        this.confirmDeleteVendorJobWithData(job);
      } else {
        this.confirmDeleteVendorJobWithoutData(job);
      }
    } else {
      this.confirmDeleteVendorJobWithoutData(job);
    }
  }

  confirmDeleteVendorJobWithoutData(job: VendorJob) {
    this._confirmationService.confirm(
      {
        key: 'confirmationDialog',
        header: 'Delete Confirmation',
        message: 'Are you sure you want to delete ' + job.name + '?',
        icon: 'fa fa-question-circle',
        accept: () => { this.deleteVendorJob(job.id); }// ,
        // reject: () => { }
      }
    );
  }

  confirmDeleteVendorJobWithData(job: VendorJob) {
    this._confirmationService.confirm(
      {
        key: 'confirmationDialog',
        header: 'Delete Confirmation',
        message: 'This job has data associated with it, are you sure you want to delete ' + job.name + '?',
        icon: 'fa fa-question-circle',
        accept: () => { this.deleteVendorJob(job.id); }// ,
        // reject: () => { }
      }
    );
  }

  deleteVendorJob(jobId: string | undefined) {
    if (!jobId) {
      return;
    }
    this._loadingService.setLoading('deleteJob', true);
    this._vendorJobService.deleteVendorJob(jobId).pipe(
      finalize(() => this._loadingService.setLoading('deleteJob', false))
    ).subscribe({
      next: result => {
        //this.vendorJobs = this.vendorJobs.filter(j => j.id !== jobId);
        this.deletedId$.next([...this.deletedId$.value, jobId]);
        this._messageService.infoMessage('Service Provider Job deleted', '');
      },
      error: error => {
        this._messageService.errorMessage(error, 'Unable to Delete Service Proivder Job');
      }
    });
  }
  async doesJobHaveData(jobId: string | undefined): Promise<boolean> {
    if (!jobId) {
      return false;
    }
    this._loadingService.setLoading('jobData', true);
    try {
      const result = await firstValueFrom(this._vendorJobService.doesCompanyHaveData(jobId), { defaultValue: false });
      return result;
    } catch (error) {
      console.error('Error checking job data:', error);
      return false;
    } finally {
      this._loadingService.setLoading('jobData', false);
    }
  }

  async getUserRoles(): Promise<UserRoles> {
    const user = await firstValueFrom(this.user$, { defaultValue: null });
    return new UserRoles(user, this._userAuthorizationService);
  }

  //#endregion

  showSpinner() {
    this._applicationContextService.showApplicationSpinner(true);
  }

  hideSpinner() {
    this._applicationContextService.hideApplicationSpinner();
  }
}
class UserRoles {
  constructor(private user: User | null, private userAuthorizationService: UserAuthorizationService) {}
  isGlobalAdmin(_jobCompanyId?: string): boolean {
    return this.user?.isGlobalAdmin ?? false;
  }
  isEditorOrAdmin(jobCompanyId: string | undefined): boolean {
    if (!this.user || !jobCompanyId) {
      return false;
    }
    return this.userAuthorizationService.isUserEditorOrAdmin(this.user, jobCompanyId, false);
  }
  isUserAdmin(jobCompanyId: string | undefined): boolean {
    if (!this.user || !jobCompanyId) {
      return false;
    }
    return this.userAuthorizationService.isUserAdmin(this.user, jobCompanyId, false);
  }
}
